import { cn, tv } from '@joyful/utils'
import { ChevronRightIcon } from 'lucide-react'
import { DropdownMenu as DropdownMenuPrimitive } from 'radix-ui'
import { Slot } from './Slot'
import type { VariantProps } from '@joyful/utils'
import { cloneElement, isValidElement, useRef } from 'react'
import type {
  ComponentProps,
  PointerEvent,
  ReactElement,
  ReactNode,
} from 'react'
import type { ItemGroupLabelProps, ItemSeparatorProps } from './Item'
import {
  Item,
  ItemContent,
  ItemEndAdornment,
  ItemGroupLabel,
  ItemIcon,
  ItemSeparator,
  ItemSubtitle,
  itemVariants,
} from './Item'

export type DropdownMenuProps = ComponentProps<
  typeof DropdownMenuPrimitive.Root
>
const DropdownMenu = DropdownMenuPrimitive.Root

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger

const DropdownMenuGroup = DropdownMenuPrimitive.Group

const DropdownMenuSub = DropdownMenuPrimitive.Sub

const DropdownMenuPortal = DropdownMenuPrimitive.Portal

const DropdownMenuRawItem = DropdownMenuPrimitive.Item

export interface DropdownMenuItemProps
  extends
    ComponentProps<typeof DropdownMenuPrimitive.Item>,
    Omit<VariantProps<typeof itemVariants>, 'isInteractive'> {
  readonly endAdornment?: ReactElement<unknown>
  readonly icon?: ReactElement<unknown>

  /**
   * @default false
   */
  readonly shouldCloseOnSelect?: boolean

  /**
   * @default false
   */
  readonly shouldPropagateClicks?: boolean
  readonly subtitle?: ReactNode
}

function DropdownMenuItem({
  children,
  className,
  colorVariant,
  disabled,
  endAdornment,
  icon,
  isInset,
  isSelected,
  selectionVariant,
  shouldCloseOnSelect = false,
  shouldPropagateClicks = false,
  size,
  subtitle,
  ...props
}: DropdownMenuItemProps) {
  const iconNode = !!icon && <ItemIcon>{icon}</ItemIcon>
  const endAdornmentNode = !!endAdornment && (
    <ItemEndAdornment>{endAdornment}</ItemEndAdornment>
  )
  const subtitleNode =
    typeof subtitle === 'string' ? (
      <ItemSubtitle>{subtitle}</ItemSubtitle>
    ) : (
      subtitle
    )

  return (
    <Item
      asChild
      colorVariant={colorVariant}
      isInset={isInset}
      isSelected={isSelected}
      selectionVariant={selectionVariant}
      size={size}
      isInteractive
    >
      <DropdownMenuRawItem
        className={className}
        disabled={disabled}
        data-disabled={disabled}
        {...props}
        onClick={(event) => {
          if (props.onSelect !== undefined && shouldPropagateClicks === false) {
            event.stopPropagation()
          }

          props.onClick?.(event)
        }}
        onSelect={(event) => {
          if (shouldCloseOnSelect === false) {
            event.preventDefault()
          }

          props.onSelect?.(event)
        }}
      >
        {props.asChild ? (
          isValidElement<{ children: ReactNode }>(children) ? (
            // oxlint-disable-next-line react/no-clone-element
            cloneElement(
              children,
              undefined,
              iconNode,
              <ItemContent>
                {children.props.children}
                {subtitleNode}
              </ItemContent>,
              endAdornmentNode,
            )
          ) : null
        ) : (
          <>
            {iconNode}
            <ItemContent>
              {children}
              {subtitleNode}
            </ItemContent>
            {endAdornmentNode}
          </>
        )}
      </DropdownMenuRawItem>
    </Item>
  )
}

export interface DropdownMenuCheckboxItemProps
  extends
    ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>,
    Omit<VariantProps<typeof itemVariants>, 'isInteractive'> {
  readonly endAdornment?: ReactElement
  readonly icon?: ReactElement
  readonly subtitle?: ReactNode
}

function DropdownMenuCheckboxItem({
  children,
  className,
  disabled,
  endAdornment,
  icon,
  isInset,
  size,
  subtitle,
  ...props
}: DropdownMenuCheckboxItemProps) {
  const iconNode = icon ? <ItemIcon>{icon}</ItemIcon> : null
  const endAdornmentNode = endAdornment ? (
    <ItemEndAdornment>{endAdornment}</ItemEndAdornment>
  ) : null
  const subtitleNode =
    typeof subtitle === 'string' ? (
      <ItemSubtitle>{subtitle}</ItemSubtitle>
    ) : (
      subtitle
    )

  return (
    <Item asChild isInset={isInset} size={size} isInteractive>
      <DropdownMenuPrimitive.CheckboxItem
        className={className}
        disabled={disabled}
        data-disabled={disabled}
        {...props}
        onClick={(event) => {
          if (props.onSelect !== undefined) {
            event.stopPropagation()
          }

          props.onClick?.(event)
        }}
        onSelect={(event) => {
          event.preventDefault()
          props.onSelect?.(event)
        }}
      >
        {props.asChild ? (
          isValidElement<{ children: ReactNode }>(children) ? (
            // oxlint-disable-next-line react/no-clone-element
            cloneElement(
              children,
              undefined,
              iconNode,
              <ItemContent>
                {children.props.children}
                {subtitleNode}
              </ItemContent>,
              endAdornmentNode,
            )
          ) : null
        ) : (
          <>
            {iconNode}
            <ItemContent>
              {children}
              {subtitleNode}
            </ItemContent>
            {endAdornmentNode}
          </>
        )}
      </DropdownMenuPrimitive.CheckboxItem>
    </Item>
  )
}

const chevronIcon = <ChevronRightIcon className="size-4" />

function DropdownMenuSubTrigger({
  children,
  className,
  endAdornment,
  icon,
  isInset,
  onPointerLeave,
  onPointerMove,
  size,
  subtitle,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.SubTrigger> &
  Pick<
    DropdownMenuItemProps,
    'size' | 'subtitle' | 'endAdornment' | 'isInset' | 'icon'
  >) {
  const lastPointerPositionRef = useRef<{ x: number; y: number } | null>(null)
  const classNames = itemVariants({
    isInset,
    isInteractive: true,
    size,
  })
  const iconNode = icon ? (
    <Slot className={classNames.icon()}>{icon}</Slot>
  ) : null
  const endAdornmentNode = endAdornment ? (
    <span className="ml-2 shrink-0">{endAdornment}</span>
  ) : null
  const subtitleNode =
    typeof subtitle === 'string' ? (
      <p className={classNames.subtitle()}>{subtitle}</p>
    ) : (
      subtitle
    )

  return (
    <DropdownMenuPrimitive.SubTrigger
      className={cn(classNames.base(), className)}
      {...props}
      onPointerMove={(event) => {
        onPointerMove?.(event)
        lastPointerPositionRef.current = {
          x: event.clientX,
          y: event.clientY,
        }
      }}
      onPointerLeave={(event) => {
        onPointerLeave?.(event)
        syncPointerDirection(event, lastPointerPositionRef.current)
      }}
    >
      {props.asChild === true ? (
        isValidElement<{ children: ReactNode }>(children) ? (
          // oxlint-disable-next-line react/no-clone-element
          cloneElement(
            children,
            undefined,
            iconNode,
            <div className={classNames.container()}>
              {children.props.children}
              {subtitleNode}
            </div>,
            endAdornmentNode,
            chevronIcon,
          )
        ) : null
      ) : (
        <>
          {iconNode}
          <div className={classNames.container()}>
            {children}
            {subtitleNode}
          </div>
          {endAdornmentNode}
          {chevronIcon}
        </>
      )}
    </DropdownMenuPrimitive.SubTrigger>
  )
}

// Radix compares each pointermove with the previous sample, and the first
// sample is x = 0, so a leave does not count as movement. Publish this
// leave's direction before Radix checks its submenu safe area.
function syncPointerDirection(
  event: PointerEvent<HTMLDivElement>,
  previousPointerPosition: { x: number; y: number } | null,
) {
  const menu = event.currentTarget.closest('[role="menu"]')
  if (
    menu === null ||
    previousPointerPosition === null ||
    event.clientX === previousPointerPosition.x
  ) {
    return
  }

  const anchorOffset = event.clientX < previousPointerPosition.x ? 1 : -1
  dispatchMenuPointerMove(menu, event.clientX + anchorOffset, event.clientY)
  dispatchMenuPointerMove(menu, event.clientX, event.clientY)
}

function dispatchMenuPointerMove(
  menu: Element,
  clientX: number,
  clientY: number,
) {
  menu.dispatchEvent(
    new PointerEvent('pointermove', {
      bubbles: true,
      cancelable: true,
      clientX,
      clientY,
      pointerType: 'mouse',
    }),
  )
}

function DropdownMenuSubContent({
  children,
  className,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.SubContent>) {
  return (
    <DropdownMenuPrimitive.SubContent
      className={cn(
        'bg-tremor-background text-tremor-content-strong animate-in data-[side=top]:fade-in data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 pointer-events-auto z-50 max-h-(--radix-dropdown-menu-content-available-height) max-w-96 min-w-44 overflow-hidden rounded-md border shadow-xs',
        className,
      )}
      {...props}
    >
      <div className={dropdownMenuViewportVariants()}>{children}</div>
    </DropdownMenuPrimitive.SubContent>
  )
}

export const dropdownMenuContentVariants = tv({
  base: 'bg-tremor-background text-tremor-content-strong data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 z-40 max-h-(--radix-dropdown-menu-content-available-height) max-w-96 min-w-44 origin-(--radix-popper-transform-origin) overflow-hidden rounded-md border shadow-xs duration-100',
  variants: {
    layer: {
      aboveToasts: 'z-[1000000000]',
    },
    width: {
      md: 'min-w-48 max-w-80',
      wide: 'min-w-72',
    },
  },
})

export const dropdownMenuViewportVariants = tv({
  base: 'max-h-[inherit] overflow-x-hidden overflow-y-auto overscroll-y-contain p-1',
  variants: {
    padding: {
      none: 'p-0',
    },
  },
})

export const dropdownMenuViewportClassName = dropdownMenuViewportVariants()

export type DropdownMenuContentProps = ComponentProps<
  typeof DropdownMenuPrimitive.Content
> &
  VariantProps<typeof dropdownMenuContentVariants> &
  Pick<VariantProps<typeof dropdownMenuViewportVariants>, 'padding'>

function DropdownMenuUnportaledContent({
  children,
  className,
  layer,
  padding,
  sideOffset = 4,
  width,
  ...props
}: DropdownMenuContentProps) {
  return (
    <DropdownMenuPrimitive.Content
      sideOffset={sideOffset}
      className={cn(dropdownMenuContentVariants({ layer, width }), className)}
      {...props}
    >
      <div className={dropdownMenuViewportVariants({ padding })}>
        {children}
      </div>
    </DropdownMenuPrimitive.Content>
  )
}

function DropdownMenuContent(props: DropdownMenuContentProps) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuUnportaledContent {...props} />
    </DropdownMenuPrimitive.Portal>
  )
}

function DropdownMenuLabel(
  props: ComponentProps<typeof DropdownMenuPrimitive.Label> &
    ItemGroupLabelProps,
) {
  return (
    <DropdownMenuPrimitive.Label asChild>
      <ItemGroupLabel {...props} />
    </DropdownMenuPrimitive.Label>
  )
}

function DropdownMenuSeparator(props: Omit<ItemSeparatorProps, 'asChild'>) {
  return (
    <ItemSeparator asChild {...props}>
      <DropdownMenuPrimitive.Separator />
    </ItemSeparator>
  )
}

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuPortal,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuRawItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
}
